import { UpdateOrderParams } from './../../types/order.type';
import { CommonModule } from '@angular/common';
import { Component, OnInit, WritableSignal, signal, input, inject, InputSignal, model, computed, output, Signal } from '@angular/core';
import { MatSelectModule } from '@angular/material/select';
import { Router, RouterModule } from '@angular/router';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { SignalsStoreService } from '../../signals-store.service';
import { FormsModule } from '@angular/forms';
import { OrderService } from '../../order.service';
import { SubscriptionsService } from '../../../settings/account/subscriptions/subscriptions.service';
import { NotificationService } from '../../notification/notification.service';
import { Subscription } from '../../../settings/account/subscriptions/subscriptions.types';
import { OnlyNumberDirective } from '../../directives/only-number.directive';
import { getProductSubscriptioninfo, openModalSkipProduct, openModalUnskipProduct } from '../../common/subscriptions.utils';
import { ModalContentService } from '../../modal-content/modal-content.service';
import { finalize, tap } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { FirebaseCrudService } from '../../firebase-crud.service';
import { EmptyMessageComponent } from '../../empty-message/empty-message.component';
import { ProductType } from '../../types/common.enums';
import { updateFirebaseOrderAndSubTrigger } from '../../common/order.utils';
import { ResolutionService } from '../../resolution.service';

@Component({
  selector: 'app-product-cart-preview',
  templateUrl: './product-cart-preview.component.html',
  styleUrl: './product-cart-preview.component.scss',
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    MatFormFieldModule,
    MatSelectModule,
    MatInputModule,
    OnlyNumberDirective,
    EmptyMessageComponent
  ]
})

export class ProductCartPreview implements OnInit {
  signalsStoreService = inject(SignalsStoreService)
  orderService = inject(OrderService)
  #router = inject(Router);
  #subscriptionsService = inject(SubscriptionsService);
  #notificationService = inject(NotificationService);
  #modalContentService = inject(ModalContentService);
  #firebaseCrudService = inject(FirebaseCrudService);
  #resoluctionService = inject(ResolutionService);

  isInputValid!: boolean;
  previousValue!: number;
  selectedQuantity: WritableSignal<any> = signal(1);
  newQuantity = model(1);
  availableQuantities: Signal<{ val: number, name: string }[]> = computed(() => this.#setUpAvailableQuantities());

  product: any = input(null, {
    alias: 'product',
    transform: (product: any) => {
      this.previousValue = product.quantity;
      this.newQuantity.set(product.quantity)
      this.isMoreThanTen.set(product.quantity >= 10);
      return product;
    }
  })

  productKey: any = input(null, {
    alias: 'productKey'
  })

  allowModification: InputSignal<boolean> = input(true);

  isInOrder = input(false);

  isSkipped = input(false);

  isSubscription = input(false);

  isPreorder = input(false);

  showUpdateButton = computed(() => this.isMoreThanTen() && (this.previousValue !== +(this.newQuantity() || 0)))

  isMoreThanTen = signal(false);

  odooOrder = computed(() => this.orderService.odooOrder());

  showProductStatusTag = environment.config.flows.order.showProductStatusTag;

  deliveryInfo: InputSignal<any> = input.required();

  outUpdateOrder = output<void>();

  orderId = computed(() => this.odooOrder()?.id ?? 0);

  totalPrice = computed(() => this.#setupProductTotalPrice());

  isSmallMobile = computed(() => this.#resoluctionService.isSmallMobile());

  productVariantText = computed(() => {
    const variantName = this.product()?.variant?.attribute?.name || '';
    const variantValue = this.product()?.variant?.attribute?.value?.name || '';
    if (variantName && variantValue)
      return `${variantName}: ${variantValue}`
    return null
  });

  productPackageText = computed(() => {
    const packageName = this.product()?.package || '';
    if (packageName) return `Package: ${packageName}`;
    return null;
  });

  productFrequencyText = computed(() => {
    const variant = this.productVariantText();
    const packageName = this.productPackageText();
    const smallMobile = this.isSmallMobile();
    const frequency = this.product().subscription?.frequency?.name || '';
    if (!frequency) return null;

    if (!smallMobile && (variant || packageName)) return `| ${frequency}`;
    return frequency;
  });

  ngOnInit(): void {
    this.isInputValid = true;
  }

  prevRemoveCartProduct() {
    const odooOrder = this.odooOrder();

    if (!odooOrder?.id)
      return;

    this.orderService
      .deleteProductOrder(+odooOrder.id, this.product().lineId)
      .pipe(finalize(() => updateFirebaseOrderAndSubTrigger(this.signalsStoreService, this.#firebaseCrudService)))
      .subscribe()
  }

  handleImageError(event: Event) {
    (event.target as HTMLImageElement).src = 'assets/images/product-placeholder.webp';
  }

  goToCustomizeBox() {
    this.signalsStoreService.showCartPreview.set(false);
    this.#router.navigate([`/shop/custom-box/${this.productKey()}/${this.product().id}`]);
  }

  unskip() {
    const sub = this.#getSubscriptionInfoByProduct(this.product());
    if (!sub) return;
    openModalUnskipProduct({
      sub,
      modalContentService: this.#modalContentService,
      subscriptionsService: this.#subscriptionsService,
      orderService: this.orderService,
      router: this.#router,
      goToCustomizeBundle: false
    });
  }

  skip() {
    const sub = this.#getSubscriptionInfoByProduct(this.product());
    if (!sub) return;
    openModalSkipProduct({
      sub,
      modalContentService: this.#modalContentService,
      subscriptionsService: this.#subscriptionsService,
      orderService: this.orderService,
      router: this.#router,
      goToCustomizeBundle: false
    });
  }

  #getSubscriptionInfoByProduct(product: any): Subscription | null {
    const subscriptions = this.signalsStoreService.subscriptions();
    return getProductSubscriptioninfo(subscriptions, product.id);
  }

  onQuantityChange() {
    if (!this.newQuantity())
      return;

    if (this.newQuantity() === 10)
      return this.isMoreThanTen.set(true);

    this.#updateProduct();
  }

  validateQuantity() {
    if (!this.newQuantity())
      return this.#notificationService.show({ text: 'Please enter a valid quantity', type: 'error' });

    this.#updateProduct();
  }

  #updateProduct() {

    const copy = this.product();
    copy.quantity = this.newQuantity();
    copy.bundleQuantityChanged = true;

    const obj: UpdateOrderParams = {
      orderId: this.orderId(),
      product: copy,
      type: this.productKey()
    };

    this.orderService
      .updateOrder(obj, this.productKey() === ProductType.subscription)
      .pipe(tap(() => updateFirebaseOrderAndSubTrigger(this.signalsStoreService, this.#firebaseCrudService)))
      .subscribe();
  }

  #setUpAvailableQuantities(): { val: number, name: string }[] {
    const res = [] as { val: number, name: string }[];
    const stockInfo = this.product()?.stockInfo?.[0] || null;
    const productQuantity = this.product().quantity || 1;
    if (!stockInfo) return res;

    const stock = stockInfo.stock + (stockInfo.limit * -1)
    const totalAvailableStock = stock < 0 ? 0 : stock;

    let available = totalAvailableStock + productQuantity;
    if (available > 10) available = 10;

    if (this.product().isBundle)
      available = 10;

    if ((available) <= 10) {
      for (let index = 1; index <= available; index++) {
        res.push({ val: index, name: (index === 10 ? '+10' : index.toString()) });
      }
    }

    return res
  }

  #setupProductTotalPrice() {
    const p = this.product();
    if (!p.isBundle) return p.totalPrice;

    const totalPrice = this.orderService.calculateProductTotalPrice(p);
    return totalPrice;
  }
}
